import { useEffect, useState } from 'react';
import { AIBudgetCard } from '../components/AIBudget';
import { InfoTooltip, Spinner } from '../components/ui';

interface UsageStats {
  request_count: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  breakdown_by_provider: Array<{
    provider_name: string;
    display_name: string;
    request_count: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_cost_usd: number;
  }>;
  breakdown_by_task: Array<{
    task_type: string;
    request_count: number;
    total_tokens: number;
    total_cost_usd: number;
  }>;
}

export function AIUsage() {
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load usage stats
      const statsRes = await fetch('/api/ai/stats');
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink-950">AI Usage & Costs</h1>
        <p className="text-sm text-ink-500 mt-1">
          Track your API usage across different AI providers
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <AIBudgetCard />

      {/* Which provider does what — informational; there is no choice to make */}
      <div className="rounded-lg border border-ink-200 p-6">
        <h2 className="mb-2 flex items-center gap-1.5 text-lg font-semibold text-ink-950">
          Who does what
          <InfoTooltip content="The provider for each job is fixed in the app. Costs below are per provider, so you can see how much each one is billing you." />
        </h2>
        <ul className="space-y-1.5 text-sm text-ink-700">
          <li>
            <span className="font-medium">Claude (Anthropic)</span> writes drafts, analyses replies,
            qualifies prospects, writes summaries and answers questions across the timeline.
          </li>
          <li>
            <span className="font-medium">OpenAI</span> only turns timeline events into search
            embeddings for the Ask page. It never writes anything.
          </li>
        </ul>
      </div>

      {/* Overall Stats */}
      {stats && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-lg border border-ink-200 p-4">
              <div className="text-2xl font-bold text-ink-950">
                {stats.request_count.toLocaleString()}
              </div>
              <div className="text-xs text-ink-500 mt-1">API Requests</div>
            </div>
            <div className="rounded-lg border border-ink-200 p-4">
              <div className="text-2xl font-bold text-ink-950">
                {stats.total_input_tokens.toLocaleString()}
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-ink-500">
                Input Tokens
                <InfoTooltip content="Tokens sent to the model — your prompts, prospect data, and conversation history." />
              </div>
            </div>
            <div className="rounded-lg border border-ink-200 p-4">
              <div className="text-2xl font-bold text-ink-950">
                {stats.total_output_tokens.toLocaleString()}
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-ink-500">
                Output Tokens
                <InfoTooltip content="Tokens the model generated in response — usually the more expensive half of the bill." />
              </div>
            </div>
            <div className="rounded-lg border border-ink-200 p-4">
              <div className="text-2xl font-bold text-ink-950">
                ${stats.total_cost_usd.toFixed(2)}
              </div>
              <div className="text-xs text-ink-500 mt-1">Total Cost</div>
            </div>
          </div>

          {/* By Provider */}
          <div className="rounded-lg border border-ink-200 p-6">
            <h2 className="text-lg font-semibold text-ink-950 mb-4">Usage by Provider</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-ink-200">
                  <tr className="text-left text-ink-600">
                    <th className="pb-3 font-semibold">Provider</th>
                    <th className="pb-3 font-semibold text-right">Requests</th>
                    <th className="pb-3 font-semibold text-right">Input Tokens</th>
                    <th className="pb-3 font-semibold text-right">Output Tokens</th>
                    <th className="pb-3 font-semibold text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.breakdown_by_provider.map((provider) => (
                    <tr key={provider.provider_name} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="py-3 text-ink-900">{provider.display_name}</td>
                      <td className="py-3 text-right text-ink-700">
                        {provider.request_count.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-ink-700">
                        {provider.total_input_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-ink-700">
                        {provider.total_output_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 text-right font-medium text-ink-900">
                        ${provider.total_cost_usd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By Task Type */}
          <div className="rounded-lg border border-ink-200 p-6">
            <h2 className="text-lg font-semibold text-ink-950 mb-4">Usage by Task Type</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-ink-200">
                  <tr className="text-left text-ink-600">
                    <th className="pb-3 font-semibold">Task</th>
                    <th className="pb-3 font-semibold text-right">Requests</th>
                    <th className="pb-3 font-semibold text-right">Total Tokens</th>
                    <th className="pb-3 font-semibold text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.breakdown_by_task.map((task) => (
                    <tr key={task.task_type} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="py-3 text-ink-900 capitalize">
                        {task.task_type.replace(/_/g, ' ')}
                      </td>
                      <td className="py-3 text-right text-ink-700">
                        {task.request_count.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-ink-700">
                        {task.total_tokens.toLocaleString()}
                      </td>
                      <td className="py-3 text-right font-medium text-ink-900">
                        ${task.total_cost_usd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
